"use client"

import { useState, useRef, useEffect } from "react"
import { marked } from "marked"
import DOMPurify from "dompurify"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Upload, FileText, Download, Loader2, X, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight } from "lucide-react"

interface MarkdownConverterProps {
  onFileChange?: (hasFile: boolean) => void
}

export function MarkdownConverter({ onFileChange }: MarkdownConverterProps) {
  const [file, setFile] = useState<File | null>(null)
  const [margin, setMargin] = useState("20mm")
  const [logoImage, setLogoImage] = useState("")
  const [logoHeight, setLogoHeight] = useState("14")
  const [headerLogo, setHeaderLogo] = useState(true)
  const [headerFontSize, setHeaderFontSize] = useState("10")
  const [headerColor, setHeaderColor] = useState("#666")
  const [headerText, setHeaderText] = useState("Header Sample")
  const [footerLogo, setFooterLogo] = useState(false)
  const [footerFontSize, setFooterFontSize] = useState("10")
  const [footerColor, setFooterColor] = useState("#666")
  const [footerText, setFooterText] = useState("")
  const [footerPageNumber, setFooterPageNumber] = useState(true)
  const [footerPageNumberAlign, setFooterPageNumberAlign] = useState("center")
  const [headerAlign, setHeaderAlign] = useState("center")
  const [footerTextAlign, setFooterTextAlign] = useState("center")
  const [showCover, setShowCover] = useState(false)
  const [coverTitle, setCoverTitle] = useState("")
  const [coverDate, setCoverDate] = useState("")
  const [coverAuthor, setCoverAuthor] = useState("")
  const [isConverting, setIsConverting] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string>("")

  useEffect(() => {
    const handleBeforePrint = () => {
      const originalTitle = document.title
      document.title = " "
      window.addEventListener(
        "afterprint",
        () => {
          document.title = originalTitle
        },
        { once: true },
      )
    }

    window.addEventListener("beforeprint", handleBeforePrint)
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint)
    }
  }, [])

  useEffect(() => {
    const updatePreview = async () => {
      if (!file) {
        setPreviewHtml("")
        return
      }

      try {
        let markdown = await file.text()
        if (showCover) {
          const coverHtml = `
<div style="text-align: center; padding: 100px 0px 0px 0px;">
  <h1 style="font-size: 36px; font-weight: bold;">${coverTitle || 'Report Title'}</h1>
</div>

<div style="text-align: right;">
  <h2>${coverDate || '2024-01-01'}, ${coverAuthor || 'John Doe'}</h2>
</div>

<div style="page-break-before: always;"></div>

`
          markdown = coverHtml + markdown
        }
        markdown = markdown.replace(/<!-- pagebreak -->/g, '\n\n<div style="page-break-before: always;"></div>\n\n');
        const content = await marked.parse(markdown)
        const sanitizedContent = DOMPurify.sanitize(content as string, { ADD_ATTR: ['style'] })
        setPreviewHtml(sanitizedContent)
      } catch (err) {
        console.error("Failed to parse markdown", err)
      }
    }

    updatePreview()
  }, [file, showCover, coverTitle, coverDate, coverAuthor])

  const error = null
  const fileInputRef = useRef<HTMLInputElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoImage(reader.result as string)
      }
      reader.readAsDataURL(selectedFile)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".md")) {
        // Simple alert for now or just ignore if error state is complicated
        return
      }
      setFile(selectedFile)
      onFileChange?.(true)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      if (!droppedFile.name.endsWith(".md")) return
      setFile(droppedFile)
      onFileChange?.(true)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const removeFile = () => {
    setFile(null)
    setPreviewHtml("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    onFileChange?.(false)
  }

  const handleDownload = async () => {
    if (!file) return;
    setIsConverting(true);
    try {
      const markdown = await file.text();
      const response = await fetch('/api/convert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          markdown,
          margin,
          logoImage,
          logoHeight,
          headerLogo,
          headerFontSize,
          headerColor,
          headerText,
          headerAlign,
          footerLogo,
          footerFontSize,
          footerColor,
          footerText,
          footerTextAlign,
          footerPageNumber,
          footerPageNumberAlign,
          showCover,
          coverTitle,
          coverDate,
          coverAuthor,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to convert PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.md', '.pdf');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download PDF', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  if (!file) {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Markdown to PDF Converter
            </CardTitle>
            <CardDescription>
              Upload a Markdown file to start customizing your PDF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-16 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="file"
                accept=".md"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-xl font-medium text-muted-foreground">
                  Click or drag and drop to select a file
                </p>
                <p className="text-sm text-muted-foreground/70">.md files only</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full space-y-6">
      <style jsx global>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .printable-page {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
          }
          main {
            padding: 0 !important;
            margin: 0 !important;
          }
          .container {
            max-width: none !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          @page {
            margin: ${margin};
          }
          .print-header {
            display: table-header-group !important;
          }
          .print-footer {
            display: table-footer-group !important;
          }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Settings */}
        <div className="lg:col-span-4 space-y-6 no-print">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Settings
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={removeFile} className="h-8 px-2 text-muted-foreground">
                  <X className="h-4 w-4 mr-1" />
                  Change File
                </Button>
              </div>
              <CardDescription className="truncate">
                {file.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="cover">Cover</TabsTrigger>
                  <TabsTrigger value="header">Header</TabsTrigger>
                  <TabsTrigger value="footer">Footer</TabsTrigger>
                </TabsList>

                <TabsContent value="cover" className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="showCover">Show Cover Page</Label>
                    <Switch
                      id="showCover"
                      checked={showCover}
                      onCheckedChange={setShowCover}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coverTitle">Title</Label>
                    <Input
                      id="coverTitle"
                      value={coverTitle}
                      onChange={(e) => setCoverTitle(e.target.value)}
                      placeholder="Report Title"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="coverDate">Date</Label>
                      <Input
                        id="coverDate"
                        value={coverDate}
                        onChange={(e) => setCoverDate(e.target.value)}
                        placeholder="2024-01-01"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coverAuthor">Author</Label>
                      <Input
                        id="coverAuthor"
                        value={coverAuthor}
                        onChange={(e) => setCoverAuthor(e.target.value)}
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="general" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="margin">Margin</Label>
                      <Input
                        id="margin"
                        value={margin}
                        onChange={(e) => setMargin(e.target.value)}
                        placeholder="20mm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="logoHeight">Logo Height (px)</Label>
                      <Input
                        id="logoHeight"
                        type="number"
                        value={logoHeight}
                        onChange={(e) => setLogoHeight(e.target.value)}
                        placeholder="14"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Logo Image</Label>
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 border rounded-md flex items-center justify-center bg-muted overflow-hidden">
                        {logoImage ? (
                          <img src={logoImage} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                        ) : (
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </Button>
                      <input
                        type="file"
                        ref={logoInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="header" className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="headerLogo">Show Logo</Label>
                    <Switch
                      id="headerLogo"
                      checked={headerLogo}
                      onCheckedChange={setHeaderLogo}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="headerFontSize">Font Size (px)</Label>
                      <Input
                        id="headerFontSize"
                        type="number"
                        value={headerFontSize}
                        onChange={(e) => setHeaderFontSize(e.target.value)}
                        placeholder="10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="headerColor">Color</Label>
                      <div className="flex gap-2">
                        <input
                          id="headerColor"
                          type="color"
                          className="w-12 p-1 h-10 block bg-transparent"
                          value={headerColor}
                          onChange={(e) => setHeaderColor(e.target.value)}
                        />
                        <Input
                          value={headerColor}
                          onChange={(e) => setHeaderColor(e.target.value)}
                          placeholder="#666"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="headerText">Header Text</Label>
                      <Input
                        id="headerText"
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        placeholder="Header Sample"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Text Align</Label>
                      <ToggleGroup type="single" value={headerAlign} onValueChange={(val) => val && setHeaderAlign(val)} className="justify-start">
                        <ToggleGroupItem value="left" aria-label="Align left">
                          <AlignLeft className="h-4 w-4" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="center" aria-label="Align center">
                          <AlignCenter className="h-4 w-4" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="right" aria-label="Align right">
                          <AlignRight className="h-4 w-4" />
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="footer" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="footerLogo">Show Logo</Label>
                      <Switch
                        id="footerLogo"
                        checked={footerLogo}
                        onCheckedChange={setFooterLogo}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="footerPageNumber">Show Page Number</Label>
                      <Switch
                        id="footerPageNumber"
                        checked={footerPageNumber}
                        onCheckedChange={setFooterPageNumber}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="footerFontSize">Font Size (px)</Label>
                      <Input
                        id="footerFontSize"
                        type="number"
                        value={footerFontSize}
                        onChange={(e) => setFooterFontSize(e.target.value)}
                        placeholder="10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="footerColor">Color</Label>
                      <div className="flex gap-2">
                        <input
                          id="footerColor"
                          type="color"
                          className="w-12 p-1 h-10 block bg-transparent"
                          value={footerColor}
                          onChange={(e) => setFooterColor(e.target.value)}
                        />
                        <Input
                          value={footerColor}
                          onChange={(e) => setFooterColor(e.target.value)}
                          placeholder="#666"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="footerText">Footer Text</Label>
                      <Input
                        id="footerText"
                        value={footerText}
                        onChange={(e) => setFooterText(e.target.value)}
                        placeholder="Footer Sample"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Text Align</Label>
                      <ToggleGroup type="single" value={footerTextAlign} onValueChange={(val) => val && setFooterTextAlign(val)} className="justify-start">
                        <ToggleGroupItem value="left" aria-label="Align left">
                          <AlignLeft className="h-4 w-4" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="center" aria-label="Align center">
                          <AlignCenter className="h-4 w-4" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="right" aria-label="Align right">
                          <AlignRight className="h-4 w-4" />
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Page Number Align</Label>
                    <ToggleGroup type="single" value={footerPageNumberAlign} onValueChange={(val) => val && setFooterPageNumberAlign(val)} className="justify-start">
                      <ToggleGroupItem value="left" aria-label="Align left">
                        <AlignLeft className="h-4 w-4" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="center" aria-label="Align center">
                        <AlignCenter className="h-4 w-4" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="right" aria-label="Align right">
                        <AlignRight className="h-4 w-4" />
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </TabsContent>
              </Tabs>

              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button
                onClick={handleDownload}
                disabled={isConverting}
                className="w-full"
                size="lg"
              >
                {isConverting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download PDF
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: HTML Preview */}
        <div className="lg:col-span-8">
          {previewHtml && (
            <div className="space-y-4 lg:sticky lg:top-4">
              <div className="flex items-center justify-between no-print">
                <h3 className="text-lg font-medium">Preview</h3>
              </div>
              <div className="lg:max-h-[calc(100vh-8rem)] overflow-y-auto border rounded-lg pr-1 bg-muted/20">
                <div
                  className="bg-white printable-page relative min-h-[297mm] shadow-sm mx-auto"
                  style={{ padding: margin, width: '210mm' }}
                >
                  <table className="w-full border-collapse">
                    <thead className="print-header">
                      <tr>
                        <td>
                          {/* Header Preview */}
                          <div
                            className="mb-8 flex items-center"
                            style={{
                              justifyContent: headerAlign === "left" ? "flex-start" : headerAlign === "right" ? "flex-end" : "center",
                              fontSize: `${headerFontSize}px`,
                              color: headerColor
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {headerLogo && logoImage && <img src={logoImage} style={{ height: `${logoHeight}px` }} alt="Logo" />}
                              <span>{headerText}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>
                          {/* Content Preview */}
                          <div
                            className="markdown-body"
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                          />
                        </td>
                      </tr>
                    </tbody>

                    <tfoot className="print-footer">
                      <tr>
                        <td>
                          {/* Footer Preview */}
                          <div
                            className="mt-8 flex flex-col w-full"
                            style={{
                              fontSize: `${footerFontSize}px`,
                              color: footerColor
                            }}
                          >
                            {(footerText || footerLogo) && (
                              <div
                                className="w-full flex items-center"
                                style={{ justifyContent: footerTextAlign === "left" ? "flex-start" : footerTextAlign === "right" ? "flex-end" : "center" }}
                              >
                                <div className="flex items-center gap-2">
                                  {footerLogo && logoImage && <img src={logoImage} style={{ height: `${logoHeight}px` }} alt="Logo" />}
                                  <span>{footerText}</span>
                                </div>
                              </div>
                            )}
                            {footerPageNumber && (
                              <div
                                className="w-full mt-2"
                                style={{ textAlign: footerPageNumberAlign as any }}
                              >
                                <span>1 / 1</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
